// src/components/Roadmap.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../utilities/AuthContext';

const API_BASE = process.env.REACT_APP_API_ENDPOINT;
const STATUS_ORDER = ['open', 'todo', 'doing', 'done'];

export default function Roadmap() {
  const [auth] = useAuth();                      // 👈 same as Navigation
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 👇 use EXACTLY the same check as your Navigation component
  const isAdmin = auth && auth.role && auth.role.includes('admin');

  useEffect(() => {
    fetchRoadmap();
  }, []);

  const fetchRoadmap = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_BASE}/feedback/roadmap`, {
        withCredentials: true,
      });
      setItems(res.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to fetch roadmap');
    } finally {
      setLoading(false);
    }
  };

  // rotate: open → todo → doing → done → open
  const getNextStatus = (current) => {
    const cur = (current || 'open').toLowerCase();
    const idx = STATUS_ORDER.indexOf(cur);
    if (idx === -1) return 'open';
    return STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
  };

  const handleStatusChange = async (item) => {
    // front-end guard; backend ALSO checks requireRole(['admin'])
    if (!isAdmin) return;

    const next = getNextStatus(item.status);

    try {
      const res = await axios.post(
        `${API_BASE}/feedback/update_status`,
        { feedbackId: item._id, status: next },
        { withCredentials: true }
      );

      const updated = res.data;
      setItems((prev) =>
        prev.map((it) => (it._id === updated._id ? updated : it))
      );
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to update status');
    }
  };

  const renderColumn = (label, statusKey) => {
    const filtered = items.filter(
      (it) => (it.status || 'open').toLowerCase() === statusKey
    );

    return (
      <div className="flex-1 bg-slate-100 rounded-lg p-3 min-h-[400px]">
        <div className='bg-sky-800 p-2 rounded-lg mb-4'>
        <h3 className="text-lg font-semibold mb-3 text-center text-slate-100">
          {label} ({filtered.length})
        </h3>
        </div>
        <div className="space-y-3">
          {filtered.length === 0 ? (
            <p className="text-md text-center my-2 text-slate-400">No items</p>
          ) : (
            filtered.map((item) => (
              <div
                key={item._id}
                className="bg-white rounded-md shadow-sm p-3 border border-slate-200 flex flex-col gap-2"
              >
                <div>
                  <p className="text-md font-semibold text-slate-900">
                    {item.title || 'Untitled'}
                  </p>
                  <p
                      className="text-sm text-slate-500 mt-1 line-clamp-3 hover:line-clamp-none 
                                transition-all duration-500 ease-in-out overflow-hidden"
                    >
                    {item.note}
                  </p>
                </div>

                <div className="flex items-center justify-between mt-2">
                  <span className="inline-flex items-center text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                    {item.vote ? item.vote.length : 0} vote
                    {item.vote && item.vote.length !== 1 ? 's' : ''}
                  </span>

                  {/* 👇 only admin can rotate */}
                  {isAdmin ? (
                    <button
                      onClick={() => handleStatusChange(item)}
                      className="text-xs px-3 py-1 rounded-md font-medium bg-sky-600 text-white hover:bg-sky-700 transition"
                    >
                      → {getNextStatus(item.status).toUpperCase()}
                    </button>
                  ) : (
                    <span className="text-xs px-3 py-1 rounded-md font-semibold text-slate-500 bg-slate-200">
                      {(item.status || 'open').toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="p-8 bg-sky-800 min-h-screen">
      <h1 className="text-3xl font-bold text-white mb-6 text-center">
        Roadmap
      </h1>

      {error && (
        <div className="mb-4 bg-red-100 text-red-700 px-4 py-2 rounded-md">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-slate-200 text-center">Loading…</p>
      ) : (
        <div className="grid grid-cols-4 gap-4 xs:grid-cols-1 sm:grid-cols-1">
          {renderColumn('Open', 'open')}
          {renderColumn('Todo', 'todo')}
          {renderColumn('Doing', 'doing')}
          {renderColumn('Done', 'done')}
        </div>
      )}
    </div>
  );
}
